import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import apiService from '../services/api';
import DemoRequestDialog from './DemoRequestDialog';

const SWIPE_THRESHOLD = 50;
const TRANSITION_MS = 500;
const GAP_REM = 2;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [visibleCount, setVisibleCount] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);

  useEffect(() => {
    const fetchProducts = async () => {
      const data = await apiService.getProducts();
      setProducts(data || []);
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const updateVisibleCount = () => {
      const width = window.innerWidth;
      if (width >= 1024) setVisibleCount(3);
      else if (width >= 768) setVisibleCount(2);
      else setVisibleCount(1);
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  const totalSlides = products.length;
  // Never show more columns than there are products
  const perView = Math.max(1, Math.min(visibleCount, totalSlides || 1));
  const isStatic = totalSlides === 0 || perView >= totalSlides;

  // Clone the edges so the carousel can loop seamlessly
  const displaySlides = isStatic
    ? products
    : [
        ...products.slice(-perView),
        ...products,
        ...products.slice(0, perView)
      ];

  // Snap back from a clone to the real slide once the transition finishes
  useEffect(() => {
    if (!isTransitioning) return;

    const resetTimeout = setTimeout(() => {
      setIsTransitioning(false);
      if (currentIndex <= 0) {
        setCurrentIndex(totalSlides);
      } else if (currentIndex >= totalSlides + perView) {
        setCurrentIndex(perView);
      }
    }, TRANSITION_MS);

    return () => clearTimeout(resetTimeout);
  }, [currentIndex, isTransitioning, totalSlides, perView]);

  // Start on the first real slide whenever the layout changes
  useEffect(() => {
    setCurrentIndex(perView);
  }, [perView]);

  const handlePrev = () => {
    if (isTransitioning || isStatic) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    if (isTransitioning || isStatic) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleDotClick = (dotIndex) => {
    if (isTransitioning || isStatic) return;
    setIsTransitioning(true);
    setCurrentIndex(dotIndex + perView);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null) return;
    const delta = touchDeltaX.current;
    touchStartX.current = null;
    touchDeltaX.current = 0;

    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
    if (delta < 0) handleNext();
    else handlePrev();
  };

  const activeDot = totalSlides
    ? (((currentIndex - perView) % totalSlides) + totalSlides) % totalSlides
    : 0;

  const translateValue = isStatic ? 0 : (currentIndex * 100) / perView;

  if (totalSlides === 0) return null;

  return (
    <section id="products" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <h2 className="text-5xl lg:text-6xl font-light tracking-tight text-gray-900 mb-6">
            Our Products
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Platforms we've built and continue to grow. Explore what they do, and request a walkthrough with our team.
          </p>
        </div>

        {/* Products Carousel */}
        <div className="relative">
          <div
            className="overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex"
              style={{
                transform: `translateX(-${translateValue}%)`,
                transition: isTransitioning ? `transform ${TRANSITION_MS}ms ease-out` : 'none',
                gap: perView === 1 ? '0' : `${GAP_REM}rem`
              }}
            >
              {displaySlides.map((product, index) => (
                <div
                  key={`${product.id}-${index}`}
                  className="flex-shrink-0"
                  style={{
                    width: perView === 1
                      ? '100%'
                      : `calc((100% - ${(perView - 1) * GAP_REM}rem) / ${perView})`
                  }}
                >
                  <Card
                    onClick={(e) => {
                      if (product.url && product.url !== '#') {
                        window.open(product.url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    className="group border-gray-200 bg-white hover:border-gray-900 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 overflow-hidden h-full cursor-pointer flex flex-col"
                  >
                    <CardContent className="p-0 h-full flex flex-col">
                      {/* Product cover: the product's landing page */}
                      <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50">
                        <img
                          src={product.image}
                          alt={`${product.name} landing page`}
                          loading="lazy"
                          draggable="false"
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                      </div>

                      {/* Product Content */}
                      <div className="p-6 flex flex-col flex-1">
                        <h3 className="text-xl font-normal text-gray-900 mb-3 flex items-center justify-between group-hover:text-blue-600 transition-colors">
                          <span>{product.name}</span>
                          <span className="text-xs text-gray-400 group-hover:text-blue-600 font-medium tracking-wide flex items-center gap-1">
                            Visit Site &rarr;
                          </span>
                        </h3>
                        <p className="text-gray-600 leading-relaxed flex-1">
                          {product.description}
                        </p>
                        <Button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveProduct(product);
                          }}
                          className="mt-6 w-full bg-black text-white hover:bg-gray-800 transition-colors duration-200"
                        >
                          Request for Demo
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {!isStatic && (
            <>
              <button
                type="button"
                onClick={handlePrev}
                disabled={isTransitioning}
                aria-label="Previous product"
                className="absolute left-0 top-1/2 z-10 -translate-y-1/2 -translate-x-1/2 rounded-full bg-white/90 border border-gray-200 shadow-sm p-3 text-gray-800 transition hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={isTransitioning}
                aria-label="Next product"
                className="absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-1/2 rounded-full bg-white/90 border border-gray-200 shadow-sm p-3 text-gray-800 transition hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        {!isStatic && (
          <div className="mt-8 flex justify-center gap-2">
            {products.map((product, dotIndex) => (
              <button
                key={product.id}
                type="button"
                onClick={() => handleDotClick(dotIndex)}
                disabled={isTransitioning}
                className={`h-2.5 w-2.5 rounded-full transition disabled:cursor-not-allowed ${
                  activeDot === dotIndex ? 'bg-gray-900' : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Show ${product.name}`}
              />
            ))}
          </div>
        )}
      </div>

      <DemoRequestDialog
        product={activeProduct}
        open={Boolean(activeProduct)}
        onOpenChange={(open) => !open && setActiveProduct(null)}
      />
    </section>
  );
};

export default Products;
